

import icalParser from './ical-parser';
import ICAL from 'ical.js';
import requests from './requests';
import xml2js from 'xml2js';
import axios from 'axios';
import moment from 'moment-timezone';
import VCard from 'vcard-creator';

// ============================================================
// DEBUG FLAGS - Set DEBUG_SIMULATE_429 to true for testing retry logic
// ============================================================
const DEBUG_SIMULATE_429 = false;
const DEBUG_429_FAIL_COUNT = 4; // Number of simulated failures before success
let debug429Counter = 0;
// ============================================================

// Utility function for delay
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Custom event for connection errors with retry callback
// This event shows a modal with a "Retry" button
const emitConnectionErrorEvent = (message, errorType, retryCallback) => {
  const event = new CustomEvent('caldav-connection-error', {
    detail: { 
      message, 
      errorType, 
      retryCallback,
      timestamp: new Date() 
    }
  });
  window.dispatchEvent(event);
};

// ============================================================
// TEST: Global function to test connection error modal from browser console
// Usage: 
//   window.testConnectionErrorModal(429)  - Rate limit error
//   window.testConnectionErrorModal(500)  - Server error (5xx)
//   window.testConnectionErrorModal()     - Default (429)
// ============================================================
if (typeof window !== 'undefined') {
  window.testConnectionErrorModal = (errorCode = 429) => {
    const errorConfigs = {
      429: {
        message: 'No se pudieron cargar los datos. El servidor está recibiendo demasiadas solicitudes.',
        errorType: 'RATE_LIMIT_EXCEEDED'
      },
      500: {
        message: 'El servidor no está disponible temporalmente. Por favor, intenta de nuevo más tarde.',
        errorType: 'SERVER_ERROR'
      },
      502: {
        message: 'El servidor no está disponible temporalmente. Por favor, intenta de nuevo más tarde.',
        errorType: 'SERVER_ERROR'
      },
      503: {
        message: 'El servidor no está disponible temporalmente. Por favor, intenta de nuevo más tarde.',
        errorType: 'SERVER_ERROR'
      }
    };

    const config = errorConfigs[errorCode] || errorConfigs[429];
    
    emitConnectionErrorEvent(
      config.message,
      config.errorType,
      () => {
        // Dispatch event to reload calendar data when retry is clicked
        window.dispatchEvent(new CustomEvent('caldav-retry-reload'));
      }
    );
  };
}
// ============================================================
/**
 * Class contructor to create the CalDav connection
 * and expose methods for interaction
 *
 * @param {Object} settings required
 ** @param {string} username required
 ** @param {string} password required
 ** @param {string} access_token required in place of @username and @passward
 ** @param {string} server required
 ** @param {string} basePath required
 ** @param {string} principalPath required
 */
export default class Caldavjs {
  constructor(settings) {
    this.username = settings.username || null;
    this.password = settings.password || null;
    this.basicAuthToken = settings.basicAuthToken || null;
    this.access_token = settings.access_token || null;
    this.server = settings.server || null;
    this.basePath = settings.basePath || null;
    this.principalPath = settings.principalPath || null;
    this.timezone = settings.timezone || null;
    this.parserLogging = settings.parserLogging || true;
    this.unifyTags = str => {
      if (!str) {
        return str;
      }
      return str.toLowerCase().replace(/^\w+:/, '');
    };
    this.XML2JS_OPTIONS = {
      tagNameProcessors: [this.unifyTags]
    };
    this.sendRequest = this.sendRequest.bind(this);
    this.extractData = this.extractData.bind(this);
    this.listEvents = this.listEvents.bind(this);
    this.searchEvents = this.searchEvents.bind(this);
    this.createCalendar = this.createCalendar.bind(this);
    this.updateCalendar = this.updateCalendar.bind(this);
    this.listCalendars = this.listCalendars.bind(this);
    this.deleteCalendar = this.deleteCalendar.bind(this);
    this.getChanges = this.getChanges.bind(this);
    this.createEvent = this.createEvent.bind(this);
    this.deleteEvent = this.deleteEvent.bind(this);
    this.addressbooks = this.addressbooks.bind(this);
    this.contacts = this.contacts.bind(this);
    this.createContact = this.createContact.bind(this);
    // this.shareCalendar = this.shareCalendar.bind(this);
  }

  async sendRequest(options, returnPayload = false, retryCount = 0) {
    const self = this;
    const MAX_RETRIES = 3;
    const BASE_DELAY = 2000; // 2 seconds base delay
    
    let url = this.server;
    if (this.basePath && options.url.indexOf(this.basePath) < 0) {
      url += this.basePath;
    }
    
    // Store original URL for retries
    const originalUrl = options.url;
    options.url = url + options.url;
    options.headers = options.headers || {};
    options.headers['Content-Type'] = options.headers['Content-Type'] || 'application/xml; charset=utf-8';
    if (this.access_token) {
      options.headers.Authorization = `Bearer ${this.access_token}`;
    } else if (this.basicAuthToken) {
      options.headers.Authorization = `Basic ${this.basicAuthToken}`;
    }
    
    // ============================================================
    // DEBUG: Simulate 429 error for testing
    // ============================================================
    if (DEBUG_SIMULATE_429 && debug429Counter < DEBUG_429_FAIL_COUNT) {
      debug429Counter++;
      console.log(`[DEBUG] Simulating 429 error (attempt ${debug429Counter}/${DEBUG_429_FAIL_COUNT})`);
      
      // Create a fake axios-like error that will be handled by our retry logic
      const fake429Error = new Error('Simulated 429 Too Many Requests');
      fake429Error.response = { status: 429, headers: {} };
      
      // Handle in the same way as a real axios error
      return Promise.resolve().then(() => {
        throw fake429Error;
      }).catch(async (error) => {
        const statusCode = error.response?.status;
        const isRateLimited = statusCode === 429;
        
        // Silent retry
        if (isRateLimited && retryCount < MAX_RETRIES) {
          const delay = 500; // Shorter delay for testing
          console.log(`[DEBUG] Silent retry ${retryCount + 1}/${MAX_RETRIES} in ${delay}ms`);
          await sleep(delay);
          options.url = originalUrl;
          return this.sendRequest(options, returnPayload, retryCount + 1);
        }
        
        // Max retries exceeded - show modal with retry callback
        console.error('[DEBUG] Rate limit exceeded after max retries - showing modal');
        emitConnectionErrorEvent(
          'No se pudieron cargar los datos. Esto puede deberse a una conexión lenta o a que el servidor está ocupado.',
          'RATE_LIMIT_EXCEEDED',
          () => {
            console.log('[DEBUG] Retry callback invoked');
            debug429Counter = 0; // Reset for retry
            options.url = originalUrl;
            return this.sendRequest(options, returnPayload, 0);
          }
        );
        return undefined;
      });
    }
    
    // Reset counter when we finally allow the request through
    if (DEBUG_SIMULATE_429 && debug429Counter >= DEBUG_429_FAIL_COUNT) {
      console.log('[DEBUG] Allowing request to proceed after simulated failures');
      debug429Counter = 0;
    }
    // ============================================================
    
    return axios({
      method: options.method,
      url: options.url,
      data: options.data,
      headers: options.headers,
      timeout: 0
    }).then(response => {
      const body = response.data;
      if (response.status >= 300) {
        throw new Error(`Status code ${response.status} - ${JSON.stringify(body, undefined, 2)}`);
      }
      return new Promise((resolve, reject) => {
        xml2js.parseString(body, self.XML2JS_OPTIONS, (err, result) => {
          if (err) {
            return reject(err);
          }
          if (!result && !returnPayload) {
            return resolve("Success");
          }
          if (!result && returnPayload) {
            return resolve({status: "Success", payload: options.data});
          }
          const parsed = (result.multistatus.response || []).map(resp => {
            const obj = {
              href: resp.href
            };
            obj.data = resp.propstat[0].prop[0];
            return obj;
          });
          resolve({
            responses: parsed,
            body: result
          });
        });
      });
    }).catch(async (error) => {
      const statusCode = error.response?.status;
      const isRateLimited = statusCode === 429;
      const isServerError = statusCode >= 500 && statusCode < 600;
      const isRetryable = isRateLimited || isServerError;
      
      // Silent retry for retryable errors
      if (isRetryable && retryCount < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, retryCount); // Exponential backoff: 2s, 4s, 8s
        const retryAfter = error.response?.headers?.['retry-after'];
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : delay;
        
        // Silent retry - only log to console
        console.warn(`[CalDAV] ${isRateLimited ? 'Rate limited (429)' : `Server error (${statusCode})`}. Silent retry ${retryCount + 1}/${MAX_RETRIES} in ${waitTime}ms`);
        
        await sleep(waitTime);
        
        // Reset URL for retry
        options.url = originalUrl;
        return this.sendRequest(options, returnPayload, retryCount + 1);
      }
      
      // Max retries exceeded for rate limiting - show modal with retry button
      if (isRateLimited) {
        console.error('[CalDAV] Rate limit exceeded after max retries');
        emitConnectionErrorEvent(
          'No se pudieron cargar los datos. El servidor está recibiendo demasiadas solicitudes.',
          'RATE_LIMIT_EXCEEDED',
          () => {
            options.url = originalUrl;
            return this.sendRequest(options, returnPayload, 0);
          }
        );
        return undefined;
      }
      
      // Handle 401 Unauthorized
      const notAuthorization = statusCode === 401 || (error.message && error.message.includes('Status code 401'));
      if (notAuthorization) {
        console.error('[CalDAV] Authentication error (401)');
        if (window !== window.top) {
          window.top.postMessage(
            JSON.stringify({
              idEvent: undefined,
              actionCancelled: true,
              selectedDate: undefined,
              removed: false,
              message: "Not Account",
              isAuth: false
            }),
            window.URL_LEXON
          );
        }

        return 'Not Account';
      }
      
      // Handle other server errors after max retries - show modal
      if (isServerError) {
        console.error(`[CalDAV] Server error (${statusCode}) after max retries`);
        emitConnectionErrorEvent(
          'El servidor no está disponible temporalmente. Por favor, intenta de nuevo más tarde.',
          'SERVER_ERROR',
          () => {
            options.url = originalUrl;
            return this.sendRequest(options, returnPayload, 0);
          }
        );
        return undefined;
      }
      
      // Log other errors
      console.error('[CalDAV] Request error:', error.message);
      return undefined;
    });
  }

  extractData(xmlObj, fields) {
    const dest = {
      href: xmlObj.href[0]
    };
    fields.forEach(field => {
      const from = Array.isArray(field) ? field[0] : field;
      const to = Array.isArray(field) ? field[1] : field.substring(field.indexOf(':') + 1);
      if (xmlObj.data[from]) {
        dest[to] = xmlObj.data[from][0];
        const quoted = (typeof dest[to] === 'string') && dest[to].match(/^"(.*)"$/);
        if (quoted) {
          dest[to] = quoted[1];
        }
      }
    });
    return dest;
  }

  /**
   * list events from a given calendar filename
   *
   * @param {object} required
   ** @param {string} filename required
   ** @param {string} start
   ** @param {string} end
   *
   * @return {array} items
   ** @return {boolean} allDay
   ** @return {string} etag
   ** @return {string} calendarData
   ** @return {string} start
   ** @return {string} end
   ** @return {string} summary
   ** @return {string} location
   ** @return {string} description
   ** @return {string} color
   ** @return {csv-string} categories
   */
   listEvents(input) {
    const self = this;
    const sameDay = input.start === input.end;
    input.start = moment(input.start).utc().format("YYYYMMDDTHHmmss");
    input.end = sameDay ? moment(input.end).add(1, 'days').utc().format("YYYYMMDDTHHmmss") : moment(input.end).utc().format("YYYYMMDDTHHmmss");
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.listEvents(input)
    })
      .then(events => {
        if (!events || !events.responses) {
          console.error('Invalid response from listEvents request');
          return [];
        }
        return events.responses.map(evt => self.extractData(evt, [
          ['getetag', 'etag'],
          ['calendar-data', 'calendarData']
        ]));
      })
      .then(events => Promise.all(events.map(evt => new Promise((resolve, reject) => {
        icalParser.convert(evt.calendarData, this.parserLogging, (err, parsed) => {
          if (err) {
            console.error("Error converting calendar data:", err);
            return reject(err);
          }
          
          try {
            // Acceso seguro a la estructura del evento
            let eventData;
            
            // Verificar si tenemos VCALENDAR y VEVENT
            if (parsed && parsed.VCALENDAR && parsed.VCALENDAR[0]) {
              if (parsed.VCALENDAR[0].VEVENT && parsed.VCALENDAR[0].VEVENT.length > 0) {
                eventData = parsed.VCALENDAR[0].VEVENT[0];
              } else {
                console.warn("No VEVENT found in parsed VCALENDAR");
                // Intentar extraer datos de la estructura actual
                eventData = parsed.VCALENDAR[0];
              }
            } else if (parsed && Object.keys(parsed).length > 0) {
              // Si no hay estructura VCALENDAR, usar el primer objeto disponible
              const firstKey = Object.keys(parsed)[0];
              eventData = parsed[firstKey] && parsed[firstKey][0] || {};
              console.warn("Unusual iCal structure, using:", firstKey);
            } else {
              console.warn("Empty or invalid iCal structure");
              eventData = {};
            }
            
            // A partir de aquí, usar eventData en lugar de parsed.VCALENDAR[0].VEVENT[0]
            const parsedKeys = Object.keys(eventData);
            
            // Extraer información de fecha de inicio
            if (evt.start === undefined) {
              const dtStart = parsedKeys.filter(prop => prop.includes('DTSTART'));
              evt.start = dtStart.length > 0 ? eventData[dtStart[0]] : null;
            }
            
            // Extraer información de fecha de fin
            if (evt.end === undefined) {
              const dtEnd = parsedKeys.filter(prop => prop.includes('DTEND'));
              evt.end = dtEnd.length > 0 ? eventData[dtEnd[0]] : null;
            }
            
            // Comprobar eventos de todo el día
            const startAllDay = parsedKeys.find(prop => prop.includes('DTSTART;VALUE=DATE'));
            const endAllDay = parsedKeys.find(prop => prop.includes('DTEND;VALUE=DATE')) || 
                             parsedKeys.find(prop => prop.includes('DTSTART;VALUE=DATE')); // A veces solo hay DTSTART para eventos de todo el día
            
            evt.allDay = !!(startAllDay && endAllDay);
            
            // Extraer y limpiar campos de texto
            const cleanTextContent = (text) => {
              return text ? text.replaceAll('\\n', '\n')
                               .replaceAll('\\\\', '%5C')
                               .replaceAll('\\', '')
                               .replaceAll('%5C', '\\')
                               .replaceAll('&amp;', '&') : text;
            };
            
            evt.summary = cleanTextContent(eventData.SUMMARY);
            evt.location = eventData.LOCATION;
            evt.description = cleanTextContent(eventData.DESCRIPTION);
            evt.color = eventData.COLOR;
            evt.categories = eventData.CATEGORIES;
            evt.lexonActuation = eventData["X-ACTUATION"];
            evt.companySelected = eventData["X-COMPANY"];
            evt.idCompanySelected = eventData["X-IDCOMPANY"];
            evt.idEventAction = eventData["X-IDEVENTACTION"];
            evt.idEventLexon = eventData["X-IDEVENTLEXON"];
            evt.visibility = eventData.CLASS;
            evt.json = eventData;
            
            resolve();
          } catch (error) {
            console.error("Error processing event data:", error);
            reject(error);
          }
        });
      }))).then(() => events));
  }

  /**
   * search events from a given calendar filename
   *
   * @param {object} required
   ** @param {string} filename required
   ** @param {string} start
   ** @param {string} end
   ** @param {string} summary
   ** @param {boolean} isSearchAll
   *
   * @return {array} items
   ** @return {boolean} allDay
   ** @return {string} etag
   ** @return {string} calendarData
   ** @return {string} start
   ** @return {string} end
   ** @return {string} summary
   ** @return {string} location
   ** @return {string} description
   ** @return {string} color
   ** @return {csv-string} categories
   */
  searchEvents(input) {
    const self = this;
    input.start = moment(input.start)
      .startOf("day")
      .hour(-1)
      .tz('Europe/Madrid')
      .utc()
      .format("YYYYMMDDTHHmmss");
    input.end = moment(input.end)
      .endOf("day")
      .tz('Europe/Madrid')
      .utc()
      .format("YYYYMMDDTHHmmss");
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.searchEvents(input)
    })
      .then(events => {
        if (!events || !events.responses) {
          console.error('Invalid response from searchEvents request');
          return [];
        }
        return events.responses.map(evt => self.extractData(evt, [
          ['getetag', 'etag'],
          ['calendar-data', 'calendarData']
        ]));
      })
      .then(events => Promise.all(events.map(evt => new Promise((resolve, reject) => {
        icalParser.convert(evt.calendarData, this.parserLogging, (err, parsed) => {
          if (err) {
            return reject(err);
          }
          parsed = parsed.VCALENDAR[0].VEVENT[0];
          const parsedKeys = Object.keys(parsed);

          if (evt.start === undefined) {
            const dtStart = parsedKeys.filter(prop => prop.includes('DTSTART'));
            evt.start = parsed[dtStart];
          }
          if (evt.end === undefined) {
            const dtEnd = parsedKeys.filter(prop => prop.includes('DTEND'));
            evt.end = parsed[dtEnd];
          }
          //Check all day events
          const startAllDay = parsedKeys.find(prop => prop.includes('DTSTART;VALUE=DATE'));
          const endAllDay = parsedKeys.find(prop => prop.includes('DTSTART;VALUE=DATE'));
          evt.allDay = (startAllDay && endAllDay) ? true : false;
          evt.summary = parsed.SUMMARY ? parsed.SUMMARY.replaceAll('\\n', '\n').replaceAll('\\\\', '%5C').replaceAll('\\', '').replaceAll('%5C', '\\').replaceAll('&amp;', '&') : parsed.SUMMARY;
          evt.location = parsed.LOCATION;
          evt.description = parsed.DESCRIPTION ? parsed.DESCRIPTION.replaceAll('\\n', '\n').replaceAll('\\\\', '%5C').replaceAll('\\', '').replaceAll('%5C', '\\').replaceAll('&amp;', '&') : parsed.DESCRIPTION;
          evt.color = parsed.COLOR;
          evt.categories = parsed.CATEGORIES;
          evt.lexonActuation = parsed["X-ACTUATION"];
          evt.companySelected = parsed["X-COMPANY"];
          evt.idCompanySelected = parsed["X-IDCOMPANY"];
          evt.idEventAction = parsed["X-IDEVENTACTION"];
          evt.idEventLexon = parsed["X-IDEVENTLEXON"];
          evt.visibility = parsed.CLASS;
          evt.json = parsed;
          resolve();
        });
      }))).then(() => events));
  }

  /**
   * event by id from a given calendar filename
   *
   * @param {object} required
   ** @param {string} filename required
   ** @param {string} start
   ** @param {string} end
   *
   * @return {array} items
   ** @return {boolean} allDay
   ** @return {string} etag
   ** @return {string} calendarData
   ** @return {string} start
   ** @return {string} end
   ** @return {string} summary
   ** @return {string} location
   ** @return {string} description
   ** @return {string} color
   ** @return {csv-string} categories
   */
  eventById(input) {
    const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.eventById(input)
    })
      .then(events => {
        if (!events || !events.responses) {
          console.error('Invalid response from eventById request');
          return [];
        }
        return events.responses.map(evt => self.extractData(evt, [
          ['getetag', 'etag'],
          ['calendar-data', 'calendarData']
        ]));
      })
      .then(events => Promise.all(events.map(evt => new Promise((resolve, reject) => {
        icalParser.convert(evt.calendarData, this.parserLogging, (err, parsed) => {
          if (err) {
            return reject(err);
          }
          parsed = parsed.VCALENDAR[0].VEVENT[0];
          const dtstart = parsed[`DTSTART;TZID=${self.timezone}`] || parsed[`DTSTART;VALUE=DATE`] || parsed.DTSTART;
          evt.allDay = typeof dtstart === 'string' && dtstart.length === 8;
          evt.start = dtstart;
          evt.end = parsed[`DTEND;TZID=${self.timezone}`] || parsed[`DTEND;VALUE=DATE`] || parsed.DTEND;
          evt.summary = parsed.SUMMARY ? parsed.SUMMARY.replaceAll('\\n', '\n').replaceAll('\\\\', '%5C').replaceAll('\\', '').replaceAll('%5C', '\\').replaceAll('&amp;', '&') : parsed.SUMMARY;
          evt.location = parsed.LOCATION;
          evt.description = parsed.DESCRIPTION ? parsed.DESCRIPTION.replaceAll('\\n', '\n').replaceAll('\\\\', '%5C').replaceAll('\\', '').replaceAll('%5C', '\\').replaceAll('&amp;', '&') : parsed.DESCRIPTION;
          evt.color = parsed.COLOR;
          evt.categories = parsed.CATEGORIES;
          evt.lexonActuation = parsed["X-ACTUATION"];
          evt.companySelected = parsed["X-COMPANY"];
          evt.idCompanySelected = parsed["X-IDCOMPANY"];
          evt.idEventAction = parsed["X-IDEVENTACTION"];
          evt.idEventLexon = parsed["X-IDEVENTLEXON"];
          evt.visibility = parsed.CLASS;
          evt.json = parsed;
          resolve();
        });
      }))).then(() => events));
  }

  eventByNameAndDate(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'PROPFIND',
      headers: {
        Depth: 1
      },
      data: requests.eventByNameAndDate(input)
    });
  }

  /**
   * Create a new calendar
   *
   * @param {object} required
   ** @param {string} name required
   ** @param {string} timezone override for settings
   ** @param {string} fllename required
   ** @param {string} description
   ** @param {string} color
   *
   * @return {string}
   */
  createCalendar(input) {
    // Crear un nuevo calendario VCALENDAR usando ical.js
    const vcalendar = new ICAL.Component(['vcalendar', [], []]);
    vcalendar.addPropertyWithValue('prodid', '-//YourApp//EN');
    vcalendar.addPropertyWithValue('version', '2.0');
    vcalendar.addPropertyWithValue('x-wr-calname', input.name);
    vcalendar.addPropertyWithValue('x-wr-timezone', input.timezone || this.timezone);
    if (input.description) vcalendar.addPropertyWithValue('x-wr-caldesc', input.description);
    if (input.color) vcalendar.addPropertyWithValue('x-apple-calendar-color', input.color);
    const calString = vcalendar.toString();
    return this.sendRequest({
      url: input.filename,
      method: 'MKCALENDAR',
      data: requests.createCalendar({
        data: calString,
        name: input.name,
        color: input.color,
        description: input.description
      })
    })
      .then(() => "Success");
  }

  /**
   * Updating a update calendar
   *
   * @param {object} required
   ** @param {string} name required
   ** @param {string} timezone override for settings
   ** @param {string} fllename required
   ** @param {string} description
   ** @param {string} color
   *
   * @return {string}
   */
  updateCalendar(input) {
    // Crear un nuevo calendario VCALENDAR usando ical.js
    const vcalendar = new ICAL.Component(['vcalendar', [], []]);
    vcalendar.addPropertyWithValue('prodid', '-//YourApp//EN');
    vcalendar.addPropertyWithValue('version', '2.0');
    vcalendar.addPropertyWithValue('x-wr-calname', input.name);
    vcalendar.addPropertyWithValue('x-wr-timezone', input.timezone || this.timezone);
    if (input.description) vcalendar.addPropertyWithValue('x-wr-caldesc', input.description);
    if (input.color) vcalendar.addPropertyWithValue('x-apple-calendar-color', input.color);
    const calString = vcalendar.toString();
    return this.sendRequest({
      url: input.filename,
      method: 'PROPPATCH',
      data: requests.updateCalendar({
        data: calString,
        name: input.name,
        color: input.color,
        description: input.description
      })
    })
      .then(() => "Success");
  }

  createPersonalCalendar() {
    const self = this;
    return this.sendRequest({
      method: 'MKCOL',
      url: `${self.basePath}/calendars/${self.username}/personal`,
      headers: {
        Depth: 0
      },
      data: requests.createPersonalCalendar()
    }).then(() => "Success")
      .catch(error => {
        console.error('Debug: index.js | createPersonalCalendar() | error ', error);
      });
  }

  /**
   * List all calendars at the resource url
   *
   * @return {array} of @return {object}
   ** @return {string} owner
   ** @return {string} displayName
   ** @return {string} description
   ** @return {object} color
   ** @return {string} ctag
   ** @return {string} syncToken
   */
  listCalendars() {
    const self = this;
    return this.sendRequest({
      method: 'PROPFIND',
      url: self.principalPath || '',
      headers: {
        Depth: 0
      },
      data: requests.principal()
    })
      .then(async principals => {
        if (principals === 'Not Account') {
          console.warn('[CalDAV] listCalendars: Not Account on principal request');
          return 'Not Account';
        }
        if (!principals || !principals.responses || !principals.responses[0]) {
          console.error('Invalid response from principal request');
          return null;
        }
        const href = principals.responses[0].data['current-user-principal'][0].href[0];
        return this.sendRequest({
          method: 'PROPFIND',
          url: href,
          headers: {
            Depth: 0
          },
          data: requests.calendarHome()
        });
      })
      .then(calhome => {
        if (calhome === 'Not Account') {
          console.warn('[CalDAV] listCalendars: Not Account on calendar home request');
          return 'Not Account';
        }
        if (!calhome || !calhome.responses || !calhome.responses[0]) {
          console.error('Invalid response from calendar home request');
          return null;
        }
        const href = calhome.responses[0].data['calendar-home-set'][0].href[0];
        return this.sendRequest({
          method: 'PROPFIND',
          url: href,
          headers: {
            Depth: 1
          },
          data: requests.calendarList()
        });
      })
      .then(calendars => {
        if (calendars === 'Not Account') {
          console.warn('[CalDAV] listCalendars: Not Account on calendar list request');
          return 'Not Account';
        }
        if (!calendars || !calendars.responses) {
          console.error('Invalid response from calendar list request');
          return null;
        }
        calendars.responses.shift();
        return calendars.responses.map(cal => {
          const obj = self.extractData(cal, ['owner', ['displayname', 'name'],
            ['calendar-color', 'color'],
            ['calendar-description', 'description'],
            ['getctag', 'ctag'],
            ['sync-token', 'syncToken'],
            ['current-user-privilege-set', 'privilege'],
            ['resourcetype', 'resourcetype']
          ]);
          if (obj.owner) {
            obj.owner = obj.owner.href[0];
          }
          return obj;
        });
      })
      .catch(err => {
        console.error('Error in listCalendars:', err);
        return null;
      });
  }

  /**
   * Delete the calendar of a given filename
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {string}
   */
  deleteCalendar(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'DELETE'
    });
  }

  /**
   * Get calendar changes since syncToken
   *
   * @param {object} required
   ** @param {string} filename required
   ** @param {string} syncToken required
   *
   * @return {object}
   ** @return {string} syncToken
   ** @return {object} changes
   *** @return {string} href
   *** @return {string} etag
   */
  getChanges(input) {
    const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      data: requests.getChanges({
        syncToken: input.syncToken
      })
    })
      .then(changes => {
        if (!changes || !changes.responses || !changes.body || !changes.body.multistatus) {
          console.error('Invalid response from getChanges request');
          return { changes: [], syncToken: null };
        }
        const syncToken = changes.body.multistatus['sync-token'][0];
        const changesData = changes.responses.map(change => self.extractData(change, [
          ['getetag', 'etag']
        ]));
        return {
          changes: changesData,
          syncToken
        };
      });
  }

  /**
   * Create a new event of a given filename
   *
   * @param {object} required
   ** @param {ISODate} start required
   ** @param {ISODate} end required
   ** @param {string} summary required
   ** @param {string} filename required
   ** @param {string} timezone override for settings
   ** @param {object} organizer
   *** @param {string} name
   *** @param {string} email
   *** @param {string} mailto
   *** @param {string} type of @param predefined individual, group, resource, room, unknown
   ** @param {string} location
   ** @param {string} description
   ** @param {string} color
   ** @param {array} categories of @param objects
   *** @param {string} name
   ** @param {object} attendees  @param objects
   *** @param {string} name
   *** @param {string} email
   *** @param {string} mailto
   ** @param {boolean} allDay
    ** @param {object} [alarms]
   ** @param {object|String} [repeating]
   *
   * @return {string}
   */
  createEvent(input, returnPayload = false) {
    const clonedInput = JSON.parse(JSON.stringify(input));
    // Convertir recordatorios de minutos a segundos si es necesario
    if (clonedInput.reminders) {
      for (let i = 0; i < clonedInput.reminders.length; i++) {
        const reminder = clonedInput.reminders[i];
        const triggerValue = parseInt(reminder.trigger, 10);
        if (!isNaN(triggerValue)) {
          clonedInput.reminders[i].trigger = triggerValue * 60;
        }
      }
    }
  
    let startDateObj, endDateObj, startDateOnly, endDateOnly;
    const timezone = clonedInput.timezone || 'Europe/Madrid';

    if (clonedInput.allDay) {
      // Fechas sin hora: se leen como calendario puro, sin pasar por ninguna zona horaria,
      // para no depender del signo del offset del dispositivo (ver bug de eventos +1 día en UTC/offset 0)
      startDateOnly = moment(clonedInput.start, ['YYYYMMDD', 'YYYY-MM-DD']);
      endDateOnly = moment(clonedInput.end, ['YYYYMMDD', 'YYYY-MM-DD']);
    } else {
      startDateObj = moment.tz(clonedInput.start, timezone).toDate();
      endDateObj = moment.tz(clonedInput.end, timezone).toDate();
    }

    if (clonedInput.allDay ? (!startDateOnly.isValid() || !endDateOnly.isValid()) : (!startDateObj || !endDateObj)) {
      throw new Error('Fecha de inicio o fin inválida');
    }
  
    try {
      // Crear VCALENDAR
      const vcalendar = new ICAL.Component(['vcalendar', [], []]);
      vcalendar.addPropertyWithValue('VERSION', '2.0');
      vcalendar.addPropertyWithValue('PRODID', '-//sebbo.net//ical-generator//EN');
    
      // Crear VEVENT
      const vevent = new ICAL.Component('vevent');
      const event = new ICAL.Event(vevent);
    
      // UID
      event.uid = clonedInput.filename.split('/').pop();
      event.sequence = 0;
    
      // DTSTAMP correcto
      const now = ICAL.Time.fromJSDate(new Date(), true);
      vevent.addPropertyWithValue('DTSTAMP', now);
      vevent.addPropertyWithValue('SEQUENCE', 0);
    
    // Fechas
    if (clonedInput.allDay) {
      const startTime = new ICAL.Time({
        year: startDateOnly.year(),
        month: startDateOnly.month() + 1,
        day: startDateOnly.date(),
        isDate: true
      });

      const endTime = new ICAL.Time({
        year: endDateOnly.year(),
        month: endDateOnly.month() + 1,
        day: endDateOnly.date(),
        isDate: true
      });
    
      event.startDate = startTime;
      event.endDate = endTime;
    
      vevent.addPropertyWithValue('X-MICROSOFT-CDO-ALLDAYEVENT', 'TRUE');
      vevent.addPropertyWithValue('X-MICROSOFT-MSNCALENDAR-ALLDAYEVENT', 'TRUE');
    } else {
      event.startDate = ICAL.Time.fromJSDate(startDateObj, true);
      event.endDate = ICAL.Time.fromJSDate(endDateObj, true);
    }
  
      // Datos principales
      event.summary = clonedInput.summary || '';
      event.description = clonedInput.description || '';
      event.location = clonedInput.location || '';
  
      // ORGANIZER
      if (clonedInput.organizer && clonedInput.organizer.email) {
        const organizer = new ICAL.Property('ORGANIZER');
        organizer.setParameter('CN', clonedInput.organizer.name || 'Organizador');
        organizer.setParameter('EMAIL', clonedInput.organizer.email);
        organizer.setValue(`mailto:${clonedInput.organizer.email}`);
        vevent.addProperty(organizer);
      }

      // Categorías
      if (clonedInput.categories && clonedInput.categories.length > 0) {
        vevent.addPropertyWithValue('CATEGORIES', clonedInput.categories[0].name);
      } else {
        vevent.addPropertyWithValue('CATEGORIES', ''); // Valor vacío si no hay categorías
      }
    
      // Color
      if (clonedInput.color) {
        vevent.addPropertyWithValue('COLOR', clonedInput.color);
      }
    
      // Visibilidad
      if (clonedInput.visibility && clonedInput.visibility === 'private') {
        vevent.addPropertyWithValue('CLASS', 'PRIVATE');
      }
    
      // Propiedades X personalizadas
      if (clonedInput.x) {
        if (clonedInput.x[0]) vevent.addPropertyWithValue('X-ACTUATION', clonedInput.x[0].value);
        if (clonedInput.x[1]) vevent.addPropertyWithValue('X-COMPANY', clonedInput.x[1].value);
        if (clonedInput.x[2]) vevent.addPropertyWithValue('X-IDEVENTACTION', clonedInput.x[2].value);
        if (clonedInput.x[3]) vevent.addPropertyWithValue('X-IDCOMPANY', clonedInput.x[3].value);
        if (clonedInput.x[4]) vevent.addPropertyWithValue('X-IDEVENTLEXON', clonedInput.x[4].value);
      }
    
      // Exclusiones
      if (clonedInput.exdate) {
        vevent.addPropertyWithValue('EXDATE', clonedInput.exdate);
        vevent.addPropertyWithValue('SEQUENCE', 1);
      }
    
      // Asistentes
      if (clonedInput.attendees) {
        clonedInput.attendees.forEach(att => {
          const attendee = new ICAL.Property('ATTENDEE');
          attendee.setParameter('CN', att.name || '');
          attendee.setParameter('EMAIL', att.email);
          attendee.setValue(`mailto:${att.email}`);
          vevent.addProperty(attendee);
        });
      }
    
      // Recordatorios / VALARMs
      if (clonedInput.reminders) {
        clonedInput.reminders.forEach(rem => {
          const triggerSeconds = parseInt(rem.trigger, 10);
          if (isNaN(triggerSeconds) || triggerSeconds <= 0) return;
        
          const valarm = new ICAL.Component('valarm');
          valarm.addPropertyWithValue('TRIGGER', `-PT${triggerSeconds}S`);
          valarm.addPropertyWithValue('ACTION', 'DISPLAY');
          valarm.addPropertyWithValue('DESCRIPTION', rem.description || 'Reminder');
          vevent.addSubcomponent(valarm);
        });
      }
    
      // Repetición (RRULE)
    if (clonedInput.repeating) {
      let rruleParts = [];
      if (clonedInput.repeating.freq) rruleParts.push(`FREQ=${clonedInput.repeating.freq}`);
      if (clonedInput.repeating.interval) rruleParts.push(`INTERVAL=${clonedInput.repeating.interval}`);
      if (clonedInput.repeating.count) rruleParts.push(`COUNT=${clonedInput.repeating.count}`);
      if (clonedInput.repeating.until) {
        const untilDate = moment.utc(clonedInput.repeating.until).format('YYYYMMDDTHHmmss') + 'Z';
        rruleParts.push(`UNTIL=${untilDate}`);
      }
      // Adaptar byDay para string o array
      if (clonedInput.repeating.byDay) {
        if (Array.isArray(clonedInput.repeating.byDay)) {
          if (clonedInput.repeating.byDay.length > 0) {
            rruleParts.push(`BYDAY=${clonedInput.repeating.byDay.join(',')}`);
          }
        } else if (typeof clonedInput.repeating.byDay === 'string' && clonedInput.repeating.byDay.length > 0) {
          rruleParts.push(`BYDAY=${clonedInput.repeating.byDay}`);
        }
      }
      if (clonedInput.repeating.byMonth) rruleParts.push(`BYMONTH=${clonedInput.repeating.byMonth}`);
      if (clonedInput.repeating.byMonthDay) rruleParts.push(`BYMONTHDAY=${clonedInput.repeating.byMonthDay}`);
      if (clonedInput.repeating.bySetPos) rruleParts.push(`BYSETPOS=${clonedInput.repeating.bySetPos}`);
      if (clonedInput.repeating.wkst) rruleParts.push(`WKST=${clonedInput.repeating.wkst}`);
    
      vevent.addPropertyWithValue('RRULE', rruleParts.join(';'));
    }
      // Agregar evento al calendario
      vcalendar.addSubcomponent(vevent);
    
      // Convertir a texto .ics
      const icsString = vcalendar.toString();
    
      // Enviar
      return this.sendRequest({
        url: clonedInput.filename,
        method: 'PUT',
        data: icsString,
        headers: {
          'Content-Type': 'text/calendar'
        }
      }, returnPayload);
    
    } catch (e) {
      throw new Error(e.toString());
    }
  }


  /**
   * Delete the calendar of a given filename
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {string}
   */
  deleteEvent(input) {
    return this.deleteCalendar(input);
  }

  /**
   * Retrieving addressbook information
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  addressbooks(input) {
    const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'PROPFIND',
      data: requests.addressbooks({})
    })
      .then(addressbooks => {
        if (!addressbooks || !addressbooks.responses) {
          console.error('Invalid response from addressbooks request');
          return { addressbooks: [] };
        }
        const addressbooksData = addressbooks.responses.map(addressbook => self.extractData(addressbook, [
          ['getctag', 'ctag'],
          ['displayname', 'name']
        ]));
        return {
          addressbooks: addressbooksData
        };
      });
  }

  /**
   * Get contacts
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  contacts(input) {
    const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.contacts({})
    })
      .then(contacts => {
        if (!contacts || !contacts.responses) {
          console.error('Invalid response from contacts request');
          return { contacts: [] };
        }
        const contactsData = contacts.responses.map(contact => self.extractData(contact, [
          ['getetag', 'etag'],
          ['address-data', 'address']
        ]));
        return {
          contacts: contactsData
        };
      });
  }

  /**
   * Get filter contacts
   *
   * @param {object} required
   ** @param {string} filename required
   ** @param {string} value required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  filterContacts(input) {
    const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.filterContacts(input.value)
    })
      .then(contacts => {
        if (!contacts || !contacts.responses) {
          console.error('Invalid response from filterContacts request');
          return { contacts: null };
        }
        const contactsData = contacts.responses.map(contact => self.extractData(contact, [
          ['getetag', 'etag'],
          ['address-data', 'address']
        ]));
        return {
          contacts: contactsData
        };
      });
  }

  /**
   * Get trashBinCalendars
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  trashBinCalendars(input) {
    // const self = this;
    return this.sendRequest({
      url: input.filename,
      method: 'REPORT',
      headers: {
        Depth: 1
      },
      data: requests.trashBinCalendars({})
    })
      .then(trashBinCalendars => ({
        trashBinCalendars
      }));
  }


  /**
   * Save a create contact
   *
   * @param {object} required
   ** @param {string} firstName required
   ** @param {string} lastName required
   ** @param {string} email required
   ** @param {string} workPhone required
   ** @param {string} uid required
   *
   * @return {string}
   */
  createContact(input) {
    let body = "";
    try {
      const vCard = new VCard();
      vCard.addName(input.name, '', '', '', '');
      vCard.addEmail(input.email);
      if (input.phone) vCard.addPhoneNumber(input.phone, 'HOME');
      if (input.mobilePhone) vCard.addPhoneNumber(input.mobilePhone, 'CELL');
      if (input.guid) vCard.addUID(input.guid);
      if (input.categories) vCard.addCategories(input.categories);
      body = vCard.toString();
    } catch (e) {
      throw new Error(e.toString());
    }
    return this.sendRequest({
      url: input.filename,
      method: 'PUT',
      data: body,
      headers: {
        'Content-Type': 'text/vcard; charset=utf-8'
      }
    })
      .then(() => this.sendRequest({
        url: input.filename,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-PROPERTY': 'CATEGORIES',
          'X-PROPERTY-REPLACE': encodeURI(input.categories)
        }
      }));
  }

  /**
   * Retrieving addressbook information
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  test(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'PROPFIND',
      data: requests.test({})
    })
      .then(res => {
        return (res);
      });
  }

  /**
   * Retrieving addressbook information
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  publishCalendarLink(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'POST',
      data: requests.publishCalendarLink({})
    })
      .then(res => {
        return res;
      });
  }

  /**
   * Retrieving addressbook information
   *
   * @param {object} required
   ** @param {string} filename required
   *
   * @return {object}
   ** @return {object} addressbooks
   *** @return {string} href
   *** @return {object} ctag
    *** @return {string} name
   */
  unpublishCalendarLink(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'POST',
      data: requests.unpublishCalendarLink({})
    })
      .then(res => {
        return res;
      });
  }


  shareCalendar(input) {
    switch (input.action) {
      case "read":
        return this.sendRequest({
          url: input.filename,
          method: 'POST',
          data: requests.shareCalendarView({contact: input.contact})
        })
          .then(res => {
            return res;
          });
      case "read-write":
        return this.sendRequest({
          url: input.filename,
          method: 'POST',
          data: requests.shareCalendarEdit({contact: input.contact})
        })
          .then(res => {
            return res;
          });
      case "delete":
        return this.sendRequest({
          url: input.filename,
          method: 'POST',
          data: requests.shareCalendarDelete({contact: input.contact})
        })
          .then(res => {
            return res;
          });
      default:
        break;
    }
  }

  invitedUsers(input) {
    return this.sendRequest({
      url: input.filename,
      method: 'PROPFIND',
      data: requests.invitedUsers()
    })
      .then(res => {
        return res;
      });
  }
}